import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Loader2, Truck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, money } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";

export default function Orders() {
  const [orders, setOrders] = useState(null);
  const [sending, setSending] = useState("");
  useEffect(() => { api.get("/orders").then((r) => setOrders(r.data)); }, []);

  const resend = async (id) => {
    setSending(id);
    try { await api.post(`/orders/${id}/resend-confirmation`); toast.success("Confirmation email sent"); }
    catch (e) { toast.error(formatApiError(e)); } finally { setSending(""); }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-8 py-12">
      <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#FF3B30] font-semibold mb-2">Account</p>
      <h1 className="font-display font-black uppercase tracking-tight text-4xl sm:text-5xl">My orders</h1>
      <div className="mt-10 space-y-4">
        {orders?.length === 0 && (
          <p data-testid="orders-empty" className="text-neutral-400">No orders yet. <Link to="/shop" className="text-white underline">Start shopping</Link>.</p>
        )}
        {orders?.map((o) => (
          <div key={o.id} data-testid={`order-${o.id}`} className="border border-white/10 bg-[#121215] p-6">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <div>
                <p className="font-mono text-xs text-neutral-500">ORDER #{o.id.slice(-8).toUpperCase()}</p>
                <p className="font-mono text-xs text-neutral-500">{new Date(o.created_at).toLocaleString()}</p>
              </div>
              <div className="flex items-center gap-4">
                <StatusBadge status={o.status} data-testid={`order-status-${o.id}`} />
                <span className="font-mono">{money(o.total)}</span>
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              {o.items.map((i, idx) => (
                <div key={idx} className="flex items-center gap-3 bg-black/30 pr-4">
                  <img src={i.image} alt={i.name} className="w-12 h-14 object-cover" />
                  <div>
                    <p className="text-sm font-semibold">{i.name}</p>
                    <p className="font-mono text-[11px] text-neutral-500">×{i.quantity} {i.customization ? i.customization.summary : [i.size, i.color].filter(Boolean).join(" · ")}</p>
                  </div>
                </div>
              ))}
            </div>
            {o.fulfillment?.shipments?.length > 0 && (
              <div data-testid={`order-tracking-${o.id}`} className="mt-4 border border-emerald-500/30 bg-emerald-500/5 p-4 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <Truck className="w-5 h-5 text-emerald-400" />
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-widest text-emerald-400">Shipped · {[o.fulfillment.shipments[0].carrier, o.fulfillment.shipments[0].service].filter(Boolean).join(" ")}</p>
                    <p className="font-mono text-sm text-white">{o.fulfillment.shipments[0].tracking_number || "Tracking number pending"}</p>
                  </div>
                </div>
                {o.fulfillment.shipments[0].tracking_url && (
                  <a href={o.fulfillment.shipments[0].tracking_url} target="_blank" rel="noreferrer" data-testid={`order-track-link-${o.id}`} className="h-9 px-4 inline-flex items-center bg-white text-black font-display uppercase font-bold text-xs tracking-wider hover:bg-neutral-200">Track package</a>
                )}
              </div>
            )}
            {o.fulfillment && !o.fulfillment.shipments?.length && o.items.some((i) => i.customization) && (
              <p data-testid={`order-fulfillment-${o.id}`} className="mt-4 font-mono text-[11px] text-neutral-400">● CUSTOM ITEMS IN PRODUCTION · TRACKING WILL APPEAR HERE</p>
            )}
            <div className="flex flex-wrap items-center justify-between gap-3 mt-4 pt-4 border-t border-white/5">
              <p className="font-mono text-[11px] text-neutral-500">
                {o.discount > 0 && <span data-testid={`order-discount-${o.id}`} className="text-emerald-400 mr-3">{o.discount_code} −{money(o.discount)}</span>}
                {o.shipping_address?.city && <span>Ships to {o.shipping_address.city}, {o.shipping_address.country}</span>}
              </p>
              <button data-testid={`order-resend-email-${o.id}`} onClick={() => resend(o.id)} disabled={sending === o.id}
                className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-wider text-neutral-400 hover:text-white disabled:opacity-50">
                {sending === o.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
                {o.confirmation_sent ? "Resend confirmation" : "Email confirmation"}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
